import { useCallback, useRef, useState } from 'react';

/**
 * Guards an async submit handler against double-submission.
 *
 * A second click (impatient user, slow network) would otherwise fire the
 * handler again and create a duplicate record — a real problem for money
 * actions like posting an invoice or recording a payment. While a submit is
 * in flight this ignores further calls via a ref (the data-integrity core),
 * and exposes a `submitting` flag so the trigger can show a disabled / busy
 * state.
 *
 * Usage:
 *   const [submitGuarded, submitting] = useSubmitGuard(handleSubmit);
 *   <button disabled={submitting} onClick={(e) => submitGuarded(e, 'save')}>
 */
export function useSubmitGuard<A extends any[]>(
    fn: (...args: A) => Promise<any> | any,
): [(...args: A) => Promise<void>, boolean] {
    const [submitting, setSubmitting] = useState(false);
    const inFlight = useRef(false);

    const run = useCallback(
        async (...args: A) => {
            if (inFlight.current) return;
            inFlight.current = true;
            setSubmitting(true);
            try {
                await fn(...args);
            } finally {
                inFlight.current = false;
                setSubmitting(false);
            }
        },
        [fn],
    );

    return [run, submitting];
}
